import { css } from '@emotion/react'
import { colors, font } from 'plume-ui'
import { desktopMinWidth } from '../../tokens/mediaquery'
import { IcomoonIcon } from '../../atoms/IcomoonIcon'
import React, { useState } from 'react'

const NUM_OF_ITEMS = 10

const stripHeadingNotation = (text: string) =>
  text.replace(/#+\s+/, '').replace('\n', '')

const extractTOC = (body: string) =>
  body.match(/^##\s.*$/gm)?.map((s) => s.toString())
export const hasTOC = (body: string) => !!extractTOC(body)

type Props = {
  body: string
}

export const TOC: React.FC<Props> = ({ body }) => {
  const [isCollapsed, setCollapsed] = useState(true)

  if (!body) {
    return null
  }

  let headings = extractTOC(body)

  if (!headings) {
    return null
  }

  let showMoreLink = false
  if (isCollapsed && headings.length > NUM_OF_ITEMS && headings) {
    headings = headings.slice(0, NUM_OF_ITEMS)
    showMoreLink = true
  }

  return (
    <div
      css={css`
        background-color: ${colors.gray30};
        margin: 50px 0 50px;
        padding: 50px 0 30px;
      `}
    >
      <div
        css={css`
          margin: 0 auto;
          padding: 0 20px;
          @media (min-width: ${desktopMinWidth}) {
            max-width: 530px;
            padding: 0;
          }
        `}
      >
        <h4
          css={css`
            color: ${colors.black10};
            font-size: 15px;
            text-align: center;
            margin: 0 0 30px;
            font-weight: ${font.weight.normal};
            letter-spacing: 0.3em;
          `}
        >
          CONTENTS
        </h4>
        <ul
          css={css`
            &&&& {
              margin: 0;
              padding: 0;
              li {
                border-bottom: 1px solid ${colors.gray50};
              }
              li > a {
                color: ${colors.black10};
                font-size: 14px;
                margin: 11px 0 7px;
                display: list-item;
                line-height: 1.5;
                &:hover {
                  color: ${colors.link};
                }
              }
            }
          `}
        >
          {headings.map((heading: string, n: number) => (
            <li key={n}>
              <TOCLink heading={stripHeadingNotation(heading)} />
            </li>
          ))}
          {showMoreLink ? (
            <li
              key="t"
              onClick={() => setCollapsed(false)}
              css={css`
                &&&&& {
                  color: ${colors.textLight};
                  padding: 30px 0 2px;
                  text-align: center;
                  cursor: pointer;
                  border-bottom: none;
                  font-size: 15px;
                }
              `}
            >
              <div>すべて表示する</div>
              <IcomoonIcon name="arrow1-down" />
            </li>
          ) : null}
        </ul>
      </div>
    </div>
  )
}

const TOCLink: React.FC<{ heading: string }> = (props) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const anchor = document.getElementById(props.heading)
    const guide = document.getElementById('guide')
    anchor ? scrollToElm(anchor) : scrollToElm(guide, 50)
  }

  const scrollToElm = (elm: HTMLElement | null, offset: number = 0) => {
    if (!elm) {
      throw Error(
        'Either heading or guide for non article plan must be rendered'
      )
    }
    const offsetTop = elm.getBoundingClientRect().top + window.pageYOffset
    window.scroll({
      top: offsetTop - offset,
      behavior: 'smooth',
    })
  }

  return (
    <a onClick={handleClick} href={`#${props.heading}`}>
      {props.heading}
    </a>
  )
}
